import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AddressAutocompleteInput from '../../components/form/AddressAutocompleteInput';
import Label from '../../components/form/Label';
import { Modal } from '../../components/ui/modal';
import { api, apiEndpoints } from '../../utils/apiService';
import { type ParsedAddress } from '../../utils/googlePlacesAddress';
import { ProspectPayload, isValidEmail, roundCoord } from './types';

interface AddProspectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

interface DuplicateInfo {
  field: string;
  existingId: number;
  existingName: string;
}

const EMPTY_FORM = {
  name: '',
  restaurant_group_name: '',
  phone: '',
  contact_first_name: '',
  contact_last_name: '',
  contact_email: '',
  contact_title: '',
  market: '',
  cuisine: '',
  neighborhood: '',
  website: '',
  notes: '',
};

const EMPTY_ADDRESS: ParsedAddress = {
  formattedAddress: '',
  address_line_1: '',
  address_line_2: '',
  city: '',
  state: '',
  postal_code: '',
  country: '',
};

const AddProspectModal: React.FC<AddProspectModalProps> = ({
  isOpen,
  onClose,
  onCreated,
}) => {
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [address, setAddress] = useState<ParsedAddress>({ ...EMPTY_ADDRESS });
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<DuplicateInfo | null>(null);

  const setField =
    (field: keyof typeof EMPTY_FORM) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleAddressInput = (value: string) => {
    // Typing manually invalidates previously parsed components.
    setAddress((prev) =>
      value === prev.formattedAddress
        ? prev
        : { ...EMPTY_ADDRESS, formattedAddress: value },
    );
  };

  const handlePlaceSelect = (parsed: ParsedAddress) => {
    setAddress(parsed);
    setForm((prev) => ({
      ...prev,
      market: prev.market || parsed.city,
    }));
  };

  const resetAndClose = () => {
    setForm({ ...EMPTY_FORM });
    setAddress({ ...EMPTY_ADDRESS });
    setErrorMessage(null);
    setDuplicate(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setDuplicate(null);

    if (!form.name.trim()) {
      setErrorMessage('Name is required.');
      return;
    }
    if (form.contact_email && !isValidEmail(form.contact_email.trim())) {
      setErrorMessage('Contact email is not a valid email address.');
      return;
    }

    const payload: ProspectPayload = {
      name: form.name.trim(),
      restaurant_group_name: form.restaurant_group_name.trim(),
      phone: form.phone.trim(),
      contact_first_name: form.contact_first_name.trim(),
      contact_last_name: form.contact_last_name.trim(),
      contact_email: form.contact_email.trim(),
      contact_title: form.contact_title.trim(),
      formatted_address: address.formattedAddress.trim(),
      address_line_1: address.address_line_1,
      address_line_2: address.address_line_2,
      city: address.city,
      state: address.state,
      postal_code: address.postal_code,
      country: address.country,
      ...(address.lat != null && address.lng != null
        ? {
            latitude: roundCoord(address.lat) ?? undefined,
            longitude: roundCoord(address.lng) ?? undefined,
          }
        : {}),
      market: form.market.trim(),
      cuisine: form.cuisine.trim(),
      neighborhood: form.neighborhood.trim(),
      website: form.website.trim(),
      notes: form.notes.trim(),
      source: 'manual',
    };

    setSubmitting(true);
    try {
      const response = await api.post(
        apiEndpoints.createSalesProspect(),
        payload,
      );
      if (!response) {
        setErrorMessage('Could not create prospect: no response from server.');
        return;
      }
      const body = await response.json();
      if (response.status === 409) {
        setDuplicate({
          field: body.field === 'phone' ? 'phone' : 'email',
          existingId: body.existing?.id,
          existingName: body.existing?.name ?? 'existing prospect',
        });
        return;
      }
      if (!response.ok) {
        const detail =
          typeof body === 'object'
            ? Object.entries(body)
                .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
                .join('; ')
            : String(body);
        setErrorMessage(`Could not create prospect. ${detail}`);
        return;
      }
      onCreated();
      resetAndClose();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Unexpected error.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = 'app-input h-11 w-full rounded-2xl px-4 py-2.5 text-sm';

  return (
    <Modal
      isOpen={isOpen}
      onClose={resetAndClose}
      className="max-w-2xl p-6 sm:p-8"
    >
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
        Add prospect
      </h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Duplicates are matched on email and phone against active prospects.
      </p>

      <form onSubmit={handleSubmit} className="mt-5 space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label htmlFor="prospect-name">Restaurant name *</Label>
            <input
              id="prospect-name"
              type="text"
              value={form.name}
              onChange={setField('name')}
              className={inputClass}
              placeholder="e.g. Rudy's Diner"
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="prospect-restaurant-group">
              Restaurant group name
            </Label>
            <input
              id="prospect-restaurant-group"
              type="text"
              value={form.restaurant_group_name}
              onChange={setField('restaurant_group_name')}
              className={inputClass}
              placeholder="e.g. Atlas Hospitality"
            />
          </div>
          <div>
            <Label htmlFor="prospect-phone">Phone</Label>
            <input
              id="prospect-phone"
              type="tel"
              value={form.phone}
              onChange={setField('phone')}
              className={inputClass}
              placeholder="+1 (718) 555-0100"
            />
          </div>
          <div>
            <Label htmlFor="prospect-website">Website</Label>
            <input
              id="prospect-website"
              type="url"
              value={form.website}
              onChange={setField('website')}
              className={inputClass}
              placeholder="https://..."
            />
          </div>
          <div>
            <Label htmlFor="prospect-contact-first-name">
              Contact first name
            </Label>
            <input
              id="prospect-contact-first-name"
              type="text"
              value={form.contact_first_name}
              onChange={setField('contact_first_name')}
              className={inputClass}
            />
          </div>
          <div>
            <Label htmlFor="prospect-contact-last-name">
              Contact last name
            </Label>
            <input
              id="prospect-contact-last-name"
              type="text"
              value={form.contact_last_name}
              onChange={setField('contact_last_name')}
              className={inputClass}
            />
          </div>
          <div>
            <Label htmlFor="prospect-contact-title">Contact title</Label>
            <input
              id="prospect-contact-title"
              type="text"
              value={form.contact_title}
              onChange={setField('contact_title')}
              className={inputClass}
              placeholder="Owner, GM, ..."
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="prospect-email">Contact email</Label>
            <input
              id="prospect-email"
              type="email"
              value={form.contact_email}
              onChange={setField('contact_email')}
              className={inputClass}
              placeholder="owner@restaurant.com"
            />
          </div>
          <div className="sm:col-span-2">
            <AddressAutocompleteInput
              label="Address"
              value={address.formattedAddress}
              onChange={handleAddressInput}
              onPlaceSelect={handlePlaceSelect}
              placeholder="Start typing the restaurant address..."
            />
            {address.city && (
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                {[address.city, address.state, address.postal_code]
                  .filter(Boolean)
                  .join(', ')}
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="prospect-market">Market</Label>
            <input
              id="prospect-market"
              type="text"
              value={form.market}
              onChange={setField('market')}
              className={inputClass}
              placeholder="NYC"
            />
          </div>
          <div>
            <Label htmlFor="prospect-cuisine">Cuisine</Label>
            <input
              id="prospect-cuisine"
              type="text"
              value={form.cuisine}
              onChange={setField('cuisine')}
              className={inputClass}
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="prospect-neighborhood">Neighborhood</Label>
            <input
              id="prospect-neighborhood"
              type="text"
              value={form.neighborhood}
              onChange={setField('neighborhood')}
              className={inputClass}
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="prospect-notes">Notes</Label>
            <textarea
              id="prospect-notes"
              value={form.notes}
              onChange={setField('notes')}
              rows={3}
              className="app-input w-full rounded-2xl px-4 py-2.5 text-sm"
            />
          </div>
        </div>

        {duplicate && (
          <div className="rounded-2xl border border-warning-300 bg-warning-50 px-4 py-3 text-sm text-warning-700 dark:border-warning-800/70 dark:bg-warning-900/20 dark:text-warning-300">
            A prospect with this {duplicate.field} already exists:{' '}
            {duplicate.existingId ? (
              <Link
                to={`/sales/prospects/${duplicate.existingId}`}
                className="font-semibold underline"
                onClick={resetAndClose}
              >
                {duplicate.existingName}
              </Link>
            ) : (
              <span className="font-semibold">{duplicate.existingName}</span>
            )}
          </div>
        )}

        {errorMessage && (
          <div className="rounded-2xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-600 dark:border-error-800/70 dark:bg-error-900/20 dark:text-error-300">
            {errorMessage}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={resetAndClose}
            className="app-button-secondary rounded-2xl px-4 py-2 text-sm font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className={`rounded-2xl bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 ${
              submitting ? 'cursor-not-allowed opacity-60' : ''
            }`}
          >
            {submitting ? 'Creating…' : 'Create prospect'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddProspectModal;
